import React, { useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { switchTenantContext } from '@/store/reset';
import { getCurrentTenant, getJwtFromCookie } from '@/features/security/utils/jwt-parser';

interface Tenant {
  id: string;
  slug: string;
  name: string;
}

/**
 * TenantSwitcher - Dropdown for switching between tenant contexts
 * Only shown if user belongs to 2+ tenants
 */
export const TenantSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userTenants,] = useState<Tenant[]>([]);
  const [currentTenantSlug, setCurrentTenantSlug] = useState<string | null>(null);

  // Get current tenant from JWT
  React.useEffect(() => {
    const loadTenantContext = async () => {
      const token = getJwtFromCookie('argus_token');
      if (token) {
        const tenantInfo = getCurrentTenant(token);
        if (tenantInfo) {
          setCurrentTenantSlug(tenantInfo.slug);
        }
      }

      try {
        // const payload = await http.get<{ items?: Tenant[] }>('/mgmt/v1/users/me/tenants');
        // const items = Array.isArray(payload?.items) ? payload.items : [];
        // if (items.length > 0) {
        //   setUserTenants(items);
        //   if (!currentTenantSlug && items[0]?.slug) {
        //     setCurrentTenantSlug(items[0].slug);
        //   }
        // }
      } catch {
        // Ignore errors: if this call fails, tenant switcher remains hidden.
      }
    };

    loadTenantContext();
  }, []);

  // Don't show if user has only 1 tenant
  if (userTenants.length < 2) {
    return null;
  }

  const handleTenantSwitch = (tenantSlug: string) => {
    if (tenantSlug === currentTenantSlug) {
      setIsOpen(false);
      return;
    }

    // Confirm switch
    const confirmed = window.confirm(
      `Switch to ${tenantSlug}? This will reload the page and clear all current data.`
    );

    if (confirmed) {
      switchTenantContext(tenantSlug);
    } else {
      setIsOpen(false);
    }
  };

  const currentTenant = userTenants.find((t) => t.slug === currentTenantSlug);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
      >
        <Building2 className="w-4 h-4 text-slate-600 dark:text-slate-400" />
        <span className="text-sm font-medium text-slate-900 dark:text-white">
          {currentTenant?.name || currentTenantSlug || 'Select Tenant'}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-slate-600 dark:text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''
            }`}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-20 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                Switch Organization
              </p>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {userTenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSwitch(tenant.slug)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-linear-to-tr from-orange-400 to-orange-600 flex items-center justify-center">
                      <span className="text-sm font-bold text-white">
                        {tenant.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {tenant.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {tenant.slug}
                      </p>
                    </div>
                  </div>

                  {tenant.slug === currentTenantSlug && (
                    <Check className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  )}
                </button>
              ))}
            </div>

            <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Switching will reload the page
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TenantSwitcher;
